import { APP_WINDOW_KEY } from '@/config/app';
import { createApp, App, h } from 'vue';

// Import and register the routes
import router from '@/router';

// Import the application's wrapper component
import RootComponent from './RootComponent.vue';

// Import client plugins to register
import { registerPlugins } from '@/plugins';

// Provide the Apollo GraphQL client to the app
import { ApolloClient, ApolloClientDefaultKey } from '@/utils/ApolloClient';

const ROUTER_READY_TIMEOUT_MS = 5000;

async function waitForRouterReady() {
  try {
    await Promise.race([
      router.isReady(),
      new Promise((_, reject) => {
        window.setTimeout(() => {
          reject(new Error(`Router did not become ready within ${ROUTER_READY_TIMEOUT_MS}ms.`));
        }, ROUTER_READY_TIMEOUT_MS);
      }),
    ]);
  } catch (error) {
    console.error('Router readiness timeout during app boot.', { error });
  }
}

// Create and Mount the App
export async function createAndMountApp(): Promise<App> {

  try {
    const app = createApp({
      render: () => h(RootComponent),
      devtools: {
        hide: import.meta.env.PROD,
      }
    })

    // Provide Apollo Client Access to the `@vue/apollo-composable` package
    app.provide(ApolloClientDefaultKey, ApolloClient);

    // Connect Routes
    app.use(router);

    // Register Plugins
    await registerPlugins(app);

    // Let the initial navigation settle before first paint so we do not
    // render an empty RouterView during boot-time redirects or auth checks.
    await waitForRouterReady();

    // Bind to the window before mounting: components can read this in `onMounted`,
    // which fires synchronously during `app.mount()` below.
    Object.assign(window, { [APP_WINDOW_KEY]: app });

    // Mount the app to the DOM
    app.mount('#app');

    return app;

  } catch (error) {
    throw error
  }
}
